/* eslint-disable no-mixed-operators */
import * as React from 'react';
import { IColumn, ConstrainMode, SelectionMode } from '@fluentui/react/lib/DetailsList';
import { connect } from 'react-redux';
import { DirectionalHint, IListProps, Link, ShimmeredDetailsList, TooltipHost } from '@fluentui/react';
import _ from 'lodash';
import { IUserProfile } from '../../Auth/types';
import { showDialog, showToastMessage } from '../../Dialog/actions';
import { ICancelRequest, IOrder } from '../types';
import Helper from '../../../core/libraries/Helper';
import Template from '../../../core/libraries/Template';
import RbLabel, { LabelSize } from '../../../bosch-react/components/label/RbLabel';
import RbButton, { ButtonSize, ButtonType } from '../../../bosch-react/components/button/RbButton';
import { cancelWorkflow, changeWorkflowIdPendingOrder, getAllPendingOrders, getMyPendingOrders, searchText } from '../services';
import Constants from '../../../core/libraries/Constants';
import ListActionsMenu from '../../../core/common/ListActionsMenu';
import DetailsListWrapper from '../../../core/components/DetailsListWrapper/DetailsListWrapper';
import Configuration from '../../../core/libraries/Configuration';
import ChangeWorkflowIdDialog from '../../../core/common/ChangeWorkflowIdDialog';
import CancelReasonDialog from '../../../core/common/CancelReasonDialog';
import { RootState } from '../../../store/store';
import { convertPeopleAndGroupObjectArrToStringArr } from '../../../utils/converter';
import RbLink from '../../../bosch-react/components/link/RbLink';

export interface MyPendingOrdersProps {
    userProfile: IUserProfile | undefined,
    showDialog: typeof showDialog,
    showToastMessage: typeof showToastMessage,
}

export interface IState {
    isAdminMode: boolean,
    pendingOrders: IOrder[] | null,
    filteredItems: IOrder[] | null,
    selectedItem: IOrder | undefined,
    showCancelDialog: boolean,
    showWorkflowIdDialog: boolean,
}

class MyPendingOrders extends React.Component<MyPendingOrdersProps, IState> {

    private timeoutId: NodeJS.Timeout | null = null;

    constructor(props: MyPendingOrdersProps) {
        super(props);
        this.state = {
            isAdminMode: false,
            selectedItem: undefined,
            pendingOrders: null,
            showCancelDialog: false,
            showWorkflowIdDialog: false,
            filteredItems: null
        };
        this.changeWorkflowID = this.changeWorkflowID.bind(this);
        this.cancelOrder = this.cancelOrder.bind(this);
    }

    async componentDidMount() {
        Helper.runNewTask(() => {
            this.getPendingOrders();
        });
    }

    shimmeredDetailsListProps: IListProps = {
        renderedWindowsAhead: 0,
        renderedWindowsBehind: 0,
    };

    searchText(value: any, columns: IColumn[]) {

    }

    changeMode() {
        this.setState({
            isAdminMode: !this.state.isAdminMode
        }, async () => {
            await this.getPendingOrders();
        })
    }

    normalModeColumns: IColumn[] = [
        {
            key: 'title',
            name: 'Title',
            fieldName: 'Title',
            currentWidth: Helper.resizeColumnByScreenWidth(10),
            minWidth: Helper.resizeColumnByScreenWidth(10),
            isResizable: true,
            isRowHeader: true,
            data: 'string',
            isPadded: true,
            onRender: (item: IOrder) => {
                return (
                    <Link target='_blank' href={Helper.generateAccessMediatorUrl(item.reportId)}>{item.title}</Link>
                );
            }
        },
        {
            key: 'authors',
            name: 'Author(s)',
            fieldName: 'Authors',
            currentWidth: Helper.resizeColumnByScreenWidth(15),
            minWidth: Helper.resizeColumnByScreenWidth(15),
            isResizable: true,
            isRowHeader: true,
            data: 'string',
            isPadded: true,
            isMultiline: true,
            onRender: (item: IOrder) => {
                return Template.renderReadOnlyPeople(convertPeopleAndGroupObjectArrToStringArr(item.authors), item.authorDisplayName);
            }
        },
        {
            key: 'orderApprover',
            name: 'Order Approver',
            fieldName: 'Approver',
            currentWidth: Helper.resizeColumnByScreenWidth(15),
            minWidth: Helper.resizeColumnByScreenWidth(15),
            isResizable: true,
            isRowHeader: true,
            data: 'string',
            isPadded: true,
            isMultiline: true,
            onRender: (item: IOrder) => {
                let approvers = !!item.approvers && item.approvers.split(';');
                return Template.renderReadOnlyPeople(!!approvers && approvers.length > 0 && approvers || []);
            }
        },
        {
            key: 'orderComment',
            name: 'Order Comments',
            fieldName: 'Comment',
            currentWidth: Helper.resizeColumnByScreenWidth(10),
            minWidth: Helper.resizeColumnByScreenWidth(10),
            isResizable: true,
            isRowHeader: true,
            data: 'string',
            isPadded: true,
            onRender: (item: IOrder) => {
                return (
                    <TooltipHost
                        directionalHint={DirectionalHint.leftCenter}
                        content={item.comment}>
                        <RbLabel size={LabelSize.Small} >{item.comment}</RbLabel>
                    </TooltipHost>
                );
            }
        },
        {
            key: 'orderDate',
            name: 'Order Date',
            fieldName: 'OrderDate',
            currentWidth: Helper.resizeColumnByScreenWidth(10),
            minWidth: Helper.resizeColumnByScreenWidth(10),
            isResizable: true,
            isRowHeader: true,
            data: 'string',
            isPadded: true,
            isMultiline: true,
            onRender: (item: IOrder) => {
                return (<RbLabel size={LabelSize.Small} >{item.created}</RbLabel>);
            }
        },
        {
            key: 'securityClass',
            name: 'Security Class',
            fieldName: 'SecurityClass',
            currentWidth: Helper.resizeColumnByScreenWidth(10),
            minWidth: Helper.resizeColumnByScreenWidth(10),
            isResizable: true,
            isRowHeader: true,
            data: 'string',
            isPadded: true,
            onRender: (item: IOrder) => {
                return (<RbLabel size={LabelSize.Small} >{Helper.getSCLongName(item.securityClass)}</RbLabel>);
            }
        }
    ];

    adminModeColumns: IColumn[] = [
        {
            key: 'orderUser',
            name: 'Order User',
            fieldName: 'OrderUser',
            currentWidth: Helper.resizeColumnByScreenWidth(12),
            minWidth: Helper.resizeColumnByScreenWidth(12),
            isResizable: true,
            isRowHeader: true,
            data: 'string',
            isPadded: true,
            isMultiline: true,
            onRender: (item: IOrder) => {
                return Template.renderReadOnlyPeople([item.createdBy]);
            }
        }
        ,
        {
            key: 'correlationId',
            name: 'Correlation ID',
            fieldName: 'CorrelationId',
            currentWidth: Helper.resizeColumnByScreenWidth(8),
            minWidth: Helper.resizeColumnByScreenWidth(8),
            isResizable: true,
            isRowHeader: true,
            data: 'string',
            isPadded: true,
            onRender: (item: IOrder) => {
                return (
                    <TooltipHost
                        directionalHint={DirectionalHint.leftCenter}
                        content={item.correlationId}>
                        <RbLabel size={LabelSize.Small} >{item.correlationId}</RbLabel>
                    </TooltipHost>
                );
            }
        }
    ]

    actionColumns: IColumn[] = [
        {
            key: 'workflowId',
            name: 'Workflow ID',
            fieldName: 'WorkflowId',
            currentWidth: Helper.resizeColumnByScreenWidth(10),
            minWidth: Helper.resizeColumnByScreenWidth(10),
            isResizable: true,
            isRowHeader: true,
            data: 'string',
            isPadded: true,
            isMultiline: true,
            onRender: (item: IOrder) => {
                return (
                    (this.state.isAdminMode === true) ?
                        <RbButton disabled type={ButtonType.Secondary} size={ButtonSize.Small} label={
                            (item.workflowId !== "" && !_.isNil(item.workflowId)) ?
                                item.workflowId : "..."}
                            title="Click here to edit this Workflow ID" onClick={() => {
                                this.setState({
                                    showWorkflowIdDialog: true,
                                    selectedItem: item
                                })
                            }} />
                        :
                        (item.workflowId !== "" && !_.isNil(item.workflowId)) &&
                        <Link target='_blank' href={process.env.REACT_APP_WORKON_BROWSE + item.workflowId}>
                            {item.workflowId}
                        </Link>
                        || "N/A"
                );
            }
        },
        {
            key: 'cancelOrder',
            name: '',
            fieldName: 'Actions',
            currentWidth: Helper.resizeColumnByScreenWidth(15),
            minWidth: Helper.resizeColumnByScreenWidth(15),
            isResizable: true,
            isRowHeader: true,
            isPadded: true,
            isMultiline: true,
            onRender: (item: IOrder) => {
                return (
                    <RbButton label="Cancel Order" size={ButtonSize.Small} onClick={() => {
                        this.setState({
                            showCancelDialog: true,
                            selectedItem: item
                        })
                    }} />
                );
            }
        }
    ]

    async getPendingOrders() {
        this.setState({
            filteredItems: null
        })
        let pendingOrders: IOrder[] = []
        try {
            if (this.state.isAdminMode) {
                pendingOrders = await getAllPendingOrders()
            }
            else {
                pendingOrders = await getMyPendingOrders()
            }
            this.setState({
                pendingOrders: pendingOrders,
                filteredItems: pendingOrders
            },
                () => {
                    this.props.showDialog(false)
                })
        } catch (error) {
            this.props.showDialog(false)
        }
    }

    async changeWorkflowID(inputWorkflowId: string) {
        if (inputWorkflowId !== "") { // OK
            let item = this.state.selectedItem;
            this.props.showDialog(Constants.DIALOG_MESSAGE.UPDATE_WORKFLOW_ID);

            try {
                let response = !!item && await changeWorkflowIdPendingOrder(this.state.selectedItem!, inputWorkflowId)
                if (response === true) {
                    this.props.showToastMessage(
                        Constants.TOAST_MESSAGE_CODE.SUCCESS,
                        Constants.CHANGE_WORKFLOW_ID.SUCCESS
                    );
                    await this.getPendingOrders();
                }
                else {
                    this.props.showToastMessage(
                        Constants.TOAST_MESSAGE_CODE.ERROR,
                        Constants.CHANGE_WORKFLOW_ID.FAILED
                    );
                }
                this.props.showDialog(false);
                this.setState({
                    selectedItem: undefined,
                    showWorkflowIdDialog: false
                });
            } catch (error) {
                this.props.showToastMessage(
                    Constants.TOAST_MESSAGE_CODE.ERROR,
                    Constants.CHANGE_WORKFLOW_ID.FAILED
                );
                this.props.showDialog(false);
                this.setState({
                    selectedItem: undefined,
                    showWorkflowIdDialog: false
                });
            }
        }
        else { // Cancel
            this.setState({
                selectedItem: undefined,
                showWorkflowIdDialog: false
            });
        }
    }

    private _onChangeText = (text: string): void => {
        if (this.timeoutId) {
            clearTimeout(this.timeoutId);
        }
        this.timeoutId = setTimeout(() => {
            this.setState({
                filteredItems: text ? [...this.state.pendingOrders ?? []].filter(i => i.title && i.title.toLowerCase().indexOf(text.toLowerCase()) > -1
                    || i.workflowId && i.workflowId.toLowerCase().indexOf(text.toLowerCase()) > -1) : [...this.state.pendingOrders ?? []],
            });
        }, 500);
    };

    async cancelOrder(reason: string) {
        if (reason !== "") { // OK
            if (!!this.props.userProfile) {
                this.props.showDialog(Constants.DIALOG_MESSAGE.CANCEL_ORDERED_REPORT);
                try {
                    var request = {
                        orderId: this.state.selectedItem?.id ?? -1,
                        comment: reason
                    } as ICancelRequest
                    let response = await cancelWorkflow(request)
                    if (response.error === false) {
                        this.props.showToastMessage(
                            Constants.TOAST_MESSAGE_CODE.SUCCESS,
                            Constants.CANCEL_REPORT.ORDER.SUCCESS
                        );
                        await this.getPendingOrders();
                    }
                    else {
                        this.props.showToastMessage(
                            Constants.TOAST_MESSAGE_CODE.ERROR,
                            Constants.CANCEL_REPORT.ORDER.FAILED
                        );
                    }
                    this.props.showDialog(false);
                    this.setState({
                        selectedItem: undefined,
                        showCancelDialog: false
                    });
                } catch (error) {
                    this.props.showToastMessage(
                        Constants.TOAST_MESSAGE_CODE.ERROR,
                        Constants.CANCEL_REPORT.ORDER.FAILED
                    );
                    this.props.showDialog(false);
                    this.setState({
                        selectedItem: undefined,
                        showCancelDialog: false
                    });
                }
            }
            else {
                this.props.showToastMessage(
                    Constants.TOAST_MESSAGE_CODE.ERROR,
                    "Something went wrong with your permission. Please try again! If the problem persists, Please click the Support button located below your avatar for assistance"
                );
                this.props.showDialog(false);
                this.setState({
                    selectedItem: undefined,
                    showCancelDialog: false
                });
            }
        }
        else { // Cancel
            this.setState({
                selectedItem: undefined,
                showCancelDialog: false
            });
        }
    }

    render() {
        let { filteredItems } = this.state
        let modeColumns = this.state.isAdminMode && [...this.normalModeColumns, ...this.adminModeColumns, ...this.actionColumns] || [...this.normalModeColumns, ...this.actionColumns];
        let element = (
            <div className="ms-Grid">
                <div className="ms-Grid-row">
                    <div className="ms-Grid-col ms-sm1" />
                    <div className="ms-Grid-col ms-sm10">
                        <div className="ms-Grid">
                            <div className="ms-Grid-row">
                                <RbLabel className="header-title" size={LabelSize.Large}>Pending Order Reports</RbLabel>
                                <ListActionsMenu
                                    columns={modeColumns}
                                    items={filteredItems || []}
                                    searchText={(value: any) => { this._onChangeText(value) }}
                                    showAdminResults={this.state.isAdminMode}
                                    getAdminData={() => {
                                        this.changeMode();
                                    }} />
                            </div>
                            <DetailsListWrapper>
                                <ShimmeredDetailsList
                                    setKey="set"
                                    compact={true}
                                    items={filteredItems || []}
                                    constrainMode={ConstrainMode.unconstrained}
                                    columns={modeColumns}
                                    selectionMode={SelectionMode.single}
                                    enableShimmer={!filteredItems}
                                    ariaLabelForShimmer="Content is being fetched"
                                    ariaLabelForGrid="Pending order details"
                                    listProps={this.shimmeredDetailsListProps}
                                    onRenderDetailsHeader={Configuration.onRenderStickyDetailsHeader}
                                    onShouldVirtualize={() => { return true }}
                                />
                            </DetailsListWrapper>
                        </div>
                    </div>
                    <div className="ms-Grid-col ms-sm1" />
                </div>
                {/* Change Workflow ID */}
                {(this.state.showWorkflowIdDialog === true) ? <ChangeWorkflowIdDialog closeDialog={this.changeWorkflowID}></ChangeWorkflowIdDialog> : ""}
                {/* Cancel dialog */}
                {(this.state.showCancelDialog === true) ? <CancelReasonDialog closeDialog={this.cancelOrder}></CancelReasonDialog> : ""}
            </div>
        );
        return element;
    }
}

const mapStateToProps = (state: RootState) => ({
    userProfile: state.auth.userProfile,
});

export default connect(mapStateToProps, {
    showDialog,
    showToastMessage,
})(MyPendingOrders);